<script lang="ts">
  /**
   * Поле вводу коментаря з лічильником символів.
   *
   * Живе ПОЗА обгорткою .no-copy сторінки матеріалу — той клас ставить
   * user-select: none на всіх нащадків і глушить contextmenu, через що в
   * Telegram WebView зникає каретка й меню «Вставити».
   */
  import { COMMENT_MAX_LENGTH } from '../lib/types';
  import { pluralUa } from '../lib/format';
  import Icon from './Icon.svelte';

  interface Props {
    submitting: boolean;
    /** Заповнене = це форма відповіді; показуємо плашку «Відповідь для …». */
    replyingToName?: string | null;
    placeholder?: string;
    autofocus?: boolean;
    onSubmit: (text: string) => void;
    onCancelReply?: () => void;
  }
  let {
    submitting,
    replyingToName = null,
    placeholder = 'Написати коментар',
    autofocus = false,
    onSubmit,
    onCancelReply,
  }: Props = $props();

  let text = $state('');
  let el = $state<HTMLTextAreaElement | undefined>(undefined);

  // Рахуємо .length (UTF-16 code units) — рівно так само, як zod на сервері.
  // Емодзі коштує 2 і тут, і там: важлива не «справедливість», а те, щоб
  // лічильник ніколи не розійшовся з валідацією.
  const left = $derived(COMMENT_MAX_LENGTH - text.length);
  const canSend = $derived(text.trim().length > 0 && left >= 0 && !submitting);

  // Автовисота: textarea росте під текст замість власного скролу всередині.
  $effect(() => {
    void text;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  });

  $effect(() => {
    if (autofocus && el) el.focus();
  });

  function submit(): void {
    if (!canSend) return;
    onSubmit(text.trim());
    text = '';
  }

  /** Публічний скид — батько чистить поле після невдалої відправки. */
  export function reset(): void {
    text = '';
  }
</script>

<div class="form">
  {#if replyingToName}
    <div class="reply-chip">
      <span>Відповідь для {replyingToName}</span>
      <button type="button" onclick={() => onCancelReply?.()} aria-label="Скасувати відповідь">
        <Icon name="x" size="xs" />
      </button>
    </div>
  {/if}

  <textarea
    bind:this={el}
    bind:value={text}
    {placeholder}
    maxlength={COMMENT_MAX_LENGTH}
    rows="2"
    disabled={submitting}
  ></textarea>

  <div class="row">
    <span class="counter" class:low={left < 100}>
      Залишилось {left} {pluralUa(left, 'символ', 'символи', 'символів')}
    </span>
    <button type="button" class="send" disabled={!canSend} onclick={submit}>
      {submitting ? 'Надсилаємо…' : 'Надіслати'}
    </button>
  </div>
</div>

<style>
  .form {
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 12px;
  }
  .reply-chip {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    background: var(--sand-soft);
    border-radius: 8px;
    padding: 6px 8px;
    margin-bottom: 8px;
    font-size: 12px;
    color: var(--text-muted);
  }
  .reply-chip button {
    background: none;
    border: none;
    padding: 2px;
    cursor: pointer;
    color: var(--text-muted);
    display: inline-flex;
  }
  textarea {
    width: 100%;
    border: none;
    background: transparent;
    resize: none;
    outline: none;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.5;
    color: var(--text);
    min-height: 44px;
    max-height: 320px;
    overflow-y: auto;
  }
  textarea::placeholder {
    color: var(--faint);
  }
  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 8px;
  }
  .counter {
    font-size: 11px;
    color: var(--faint);
  }
  .counter.low {
    color: var(--status-inactive);
  }
  .send {
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
    border: none;
    border-radius: 999px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: filter 0.15s, opacity 0.15s;
  }
  .send:disabled {
    opacity: 0.45;
    cursor: default;
  }
  .send:not(:disabled):active {
    filter: brightness(1.05);
  }
</style>
